'use client';

import { useState } from 'react';

import { useRouter } from 'next/navigation';

import { useOnCheckoutEnded } from '@ps/features/checkout/notification/useOnCheckoutEnded';
import { useOnCheckoutSuccess } from '@ps/features/checkout/notification/useOnCheckoutSuccess';

/**
 * Owns what happens to `/<url_code>/subscribe` once a community-subscription
 * checkout launched from it completes and the buyer dismisses the success
 * screen. Both halves live here because they're one handoff:
 *
 *  1. `handedOff` tells the host to render nothing. Without it the checkout pops
 *     back onto this still-mounted route and the picker REPAINTS — offering the
 *     plan the buyer just bought — until the navigation below commits. Same
 *     reason `useIsSubscribeBlocked` hides the whole shell rather than its
 *     contents: the chrome is what's visible.
 *
 *     It flips on `success` — when the PAYMENT finalizes — not on the dismiss.
 *     The dismiss is too late to be sure: `useCloseCheckoutModal` calls
 *     `router.back()` in the same click handler that would set the flag, so the
 *     browser can paint the revealed picker for a frame before React commits the
 *     hide (the millisecond flash). At `success` the buyer is still on the
 *     success screen, this route is mounted underneath it and covered by the
 *     checkout overlay, so the hide is already committed — and invisible —
 *     whenever they get around to tapping Done.
 *  2. the `ended` navigation itself. An explicit `successRedirectUrl` (a
 *     subscription-gated digital-product button's PDP, threaded in as
 *     `redirect_to`) means the buyer should land THERE — `router.back()` would
 *     return to wherever the flow was opened FROM, never visiting the
 *     destination the gate was guarding. `replace` (not `push`) swaps out this
 *     now-stale `/subscribe` entry rather than stacking the PDP on top of it, so
 *     Back from the PDP returns to the opener, same as the no-target case.
 *
 * It belongs to the HOST, not to the picker body: the host stays mounted while
 * hidden, so the navigation still runs. Owning only half of it in a component
 * that `handedOff` unmounts would strand the buyer on the bio.
 */
export function useSubscribeCheckoutHandoff(
  successRedirectUrl?: string,
): boolean {
  const router = useRouter();
  const [handedOff, setHandedOff] = useState(false);

  useOnCheckoutSuccess(event => {
    if (event.type === 'community_membership_subscription') {
      setHandedOff(true);
    }
  });

  useOnCheckoutEnded(event => {
    if (event.type !== 'community_membership_subscription') return;
    if (successRedirectUrl) {
      router.replace(successRedirectUrl);
      return;
    }
    router.back();
  });

  return handedOff;
}
